'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { GlobeButton } from '@/components/globe/globe-button';
import { ProposalsHowItWorksModal } from '@/components/how-it-works/proposals-how-it-works-modal';
import { WhatIsAProposalModal } from '@/components/how-it-works/what-is-a-proposal-modal';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Bookmark02Icon, Bookmark02IconSolid, FilterVerticalIcon, HelpCircleIcon, Search02Icon } from '@/components/ui/icon/glyphs';
import { onEvent } from '@/events';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { type ProposalAuthContinuation, type ProposalAuthContinuationFailureReason, readProposalAuthContinuation } from '@/lib/launchpad/proposal-auth-continuation';
import { logger } from '@/lib/telemetry/logger';
import { LISTING_INDEX_PAGE_STACK_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { useLayoutStore } from '@/stores/layout';
import { CreateProposalDialog } from './create-proposal-dialog';
import { LaunchpadHeader } from './launchpad-header';
import { ProposalFeed } from './proposal-feed';

// The launch / help / what-is dialogs are mutually-exclusive modal overlays —
// one discriminated state models them better than three parallel flags.
type ActiveModal = {
  kind: 'create';
  continuation?: ProposalAuthContinuation;
  continuationFailure?: ProposalAuthContinuationFailureReason;
} | {
  kind: 'help';
} | {
  kind: 'whatIs';
};
export function LaunchpadFeedLayout() {
  const $ = _c(76);
  const t = useTranslations("launchpad.feed");
  const tMarkets = useTranslations("markets");
  const searchParams = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => searchParams.get("create") === "1" ? {
      kind: "create"
    } : null;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [activeModal, setActiveModal] = useState(t0);
  let t1;
  if ($[2] !== searchParams) {
    t1 = () => searchParams.get("view") === "watchlist";
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [watchlistActive, setWatchlistActive] = useState(t1);
  const continuationCheckedRef = useRef(false);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setActiveModal(null);
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const closeModal = t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (continuationCheckedRef.current) {
        return;
      }
      continuationCheckedRef.current = true;
      const frameId = window.requestAnimationFrame(() => {
        const result = readProposalAuthContinuation();
        if (result.status === "ready") {
          setActiveModal({
            kind: "create",
            continuation: result.continuation
          });
          return;
        }
        if (result.status === "stale") {
          logger.warn("proposal_creation.auth_resume_failed", {
            reason: result.reason
          });
          onEvent("proposal_auth_continuation_failed", {
            flow: result.flow,
            sourceStep: 4,
            outcome: result.reason,
            resumeMechanism: "session_remount",
            elapsedMs: result.createdAt === null ? 0 : Math.max(0, Date.now() - result.createdAt)
          });
          setActiveModal({
            kind: "create",
            continuationFailure: result.reason
          });
        }
      });
      return () => window.cancelAnimationFrame(frameId);
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const searchModalOpen = useLayoutStore(_temp);
  const setSearchOpen = useLayoutStore(_temp2);
  const {
    ensureAuthenticated
  } = useAuthGate();
  let t5;
  if ($[7] !== ensureAuthenticated || $[8] !== watchlistActive) {
    t5 = function handleWatchlistToggle() {
      if (!ensureAuthenticated()) {
        return;
      }
      const next = !watchlistActive;
      setWatchlistActive(next);
      onEvent("proposal_watchlist_toggled", {
        active: next
      });
    };
    $[7] = ensureAuthenticated;
    $[8] = watchlistActive;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleWatchlistToggle = t5;
  let t6;
  if ($[10] !== setSearchOpen) {
    t6 = () => setSearchOpen(true);
    $[10] = setSearchOpen;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== tMarkets) {
    t7 = tMarkets("searchPlaceholder");
    $[12] = tMarkets;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = searchModalOpen && "bg-background-tertiary";
  let t9;
  if ($[14] !== t8) {
    t9 = cn("text-foreground-tertiary hover:bg-background-tertiary", t8);
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t9) {
    t10 = <AnimatedButton variant="tertiary" size="default" prefix={Search02Icon} onClick={t6} aria-label={t7} className={t9} />;
    $[16] = t6;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <GlobeButton mode="launch" />;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setFilterOpen(true);
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t) {
    t13 = t("filterButton");
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = filterOpen && "bg-background-tertiary";
  let t15;
  if ($[24] !== t14) {
    t15 = cn("text-foreground-tertiary hover:bg-background-tertiary", t14);
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t13 || $[27] !== t15) {
    t16 = <AnimatedButton variant="tertiary" size="default" prefix={FilterVerticalIcon} onClick={t12} aria-label={t13} className={t15} />;
    $[26] = t13;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setActiveModal({
      kind: "help"
    });
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t) {
    t18 = t("helpAriaLabel");
    $[30] = t;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = activeModal?.kind === "help" && "bg-background-tertiary";
  let t20;
  if ($[32] !== t19) {
    t20 = cn("text-foreground-tertiary hover:bg-background-tertiary", t19);
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t18 || $[35] !== t20) {
    t21 = <AnimatedButton variant="tertiary" size="default" prefix={HelpCircleIcon} onClick={t17} aria-label={t18} className={t20} />;
    $[34] = t18;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  const t22 = watchlistActive ? Bookmark02IconSolid : Bookmark02Icon;
  let t23;
  if ($[37] !== t) {
    t23 = t("watchlist");
    $[37] = t;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const t24 = watchlistActive && "bg-background-tertiary";
  let t25;
  if ($[39] !== t24) {
    t25 = cn("text-foreground-tertiary hover:bg-background-tertiary", t24);
    $[39] = t24;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== handleWatchlistToggle || $[42] !== t22 || $[43] !== t23 || $[44] !== t25 || $[45] !== watchlistActive) {
    t26 = <AnimatedButton variant="tertiary" size="default" prefix={t22} onClick={handleWatchlistToggle} aria-label={t23} aria-pressed={watchlistActive} className={t25} />;
    $[41] = handleWatchlistToggle;
    $[42] = t22;
    $[43] = t23;
    $[44] = t25;
    $[45] = watchlistActive;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t10 || $[48] !== t16 || $[49] !== t21 || $[50] !== t26) {
    t27 = <div className="flex shrink-0 items-center gap-0">{t10}{t11}{t16}{t21}{t26}</div>;
    $[47] = t10;
    $[48] = t16;
    $[49] = t21;
    $[50] = t26;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  const actions = t27;
  let t28;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setActiveModal({
      kind: "create"
    });
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== actions) {
    t29 = <LaunchpadHeader actions={actions} onLaunchMarketClick={t28} />;
    $[53] = actions;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== filterOpen || $[56] !== watchlistActive) {
    t30 = <ProposalFeed showCreateButton={true} filterOpen={filterOpen} onFilterOpenChange={setFilterOpen} watchlistActive={watchlistActive} onWatchlistActiveChange={setWatchlistActive} />;
    $[55] = filterOpen;
    $[56] = watchlistActive;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  const t31 = activeModal?.kind === "create";
  const t32 = activeModal?.kind === "create" ? activeModal.continuation : undefined;
  const t33 = activeModal?.kind === "create" ? activeModal.continuationFailure : undefined;
  let t34;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = isOpen => {
      if (!isOpen) {
        closeModal();
      }
    };
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== t31 || $[60] !== t32 || $[61] !== t33) {
    t35 = <CreateProposalDialog open={t31} continuation={t32} continuationFailure={t33} onOpenChange={t34} />;
    $[59] = t31;
    $[60] = t32;
    $[61] = t33;
    $[62] = t35;
  } else {
    t35 = $[62];
  }
  const t36 = activeModal?.kind === "help";
  let t37;
  let t38;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = isOpen_0 => {
      if (!isOpen_0) {
        closeModal();
      }
    };
    t38 = () => setActiveModal({
      kind: "whatIs"
    });
    $[63] = t37;
    $[64] = t38;
  } else {
    t37 = $[63];
    t38 = $[64];
  }
  let t39;
  if ($[65] !== t36) {
    t39 = <ProposalsHowItWorksModal open={t36} onOpenChange={t37} onWhatIsProposal={t38} />;
    $[65] = t36;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  const t40 = activeModal?.kind === "whatIs";
  let t41;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = isOpen_1 => {
      if (!isOpen_1) {
        closeModal();
      }
    };
    $[67] = t41;
  } else {
    t41 = $[67];
  }
  let t42;
  if ($[68] !== t40) {
    t42 = <WhatIsAProposalModal open={t40} onOpenChange={t41} />;
    $[68] = t40;
    $[69] = t42;
  } else {
    t42 = $[69];
  }
  let t43;
  if ($[70] !== t29 || $[71] !== t30 || $[72] !== t35 || $[73] !== t39 || $[74] !== t42) {
    t43 = <div className={LISTING_INDEX_PAGE_STACK_CLASS}>{t29}{t30}{t35}{t39}{t42}</div>;
    $[70] = t29;
    $[71] = t30;
    $[72] = t35;
    $[73] = t39;
    $[74] = t42;
    $[75] = t43;
  } else {
    t43 = $[75];
  }
  return t43;
}
function _temp2(s_0) {
  return s_0.setSearchOpen;
}
function _temp(s) {
  return s.searchOpen;
}